import { Chip } from '@heroui/react';
import React, { FC } from 'react';
import { useParams } from 'react-router-dom';

import { Flex, PageTitle, PageWrapper, Spinner } from '../../../components';
import { useGetInMailDocumentFromFirestore } from '../../../queries';
import { inMailTitleToString } from '../../../types';

const targetLabel = (data: ReturnType<typeof useGetInMailDocumentFromFirestore>['data']): string => {
  const t = data?.target;
  if (!t) return '—';
  if (t.mode === 'all') return 'All users';
  if (t.mode === 'organizations') return `Organizations (${t.organizationIds?.length ?? 0})`;
  return 'Selected users';
};

export const InMailDetail: FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useGetInMailDocumentFromFirestore(id);

  if (isLoading) {
    return <PageWrapper><Spinner /></PageWrapper>;
  }
  if (!data) {
    return <PageWrapper><PageTitle title="InMail not found" /></PageWrapper>;
  }

  const total = data.recipientCount ?? 0;
  const sent = data.sentCount ?? 0;
  const failed = data.failedCount ?? 0;
  const failedRecipients = data.failedRecipients ?? [];

  return (
    <PageWrapper>
      <PageTitle title={inMailTitleToString(data.title) || 'InMail'} />
      <Flex direction="column" gap="20px" margin="20px 0">
        <Flex gap="10px">
          <Chip variant="flat">{`Target: ${targetLabel(data)}`}</Chip>
          <Chip variant="flat">{`Recipients: ${total}`}</Chip>
          <Chip color="success" variant="flat">{`Sent: ${sent}`}</Chip>
          <Chip color={failed > 0 ? 'danger' : 'default'} variant="flat">{`Failed: ${failed}`}</Chip>
        </Flex>

        {failed > 0 ? (
          <div>
            <h3 className="text-sm font-semibold mb-2">Failed recipients</h3>
            <ul className="text-sm text-default-600 list-disc pl-5">
              {failedRecipients.map((r) => (
                <li key={r.userId || r.email}>
                  {r.email}
                  {r.error ? ` — ${r.error}` : ''}
                </li>
              ))}
            </ul>
            {failedRecipients.length < failed && (
              <p className="text-xs text-default-400 mt-2">
                {`Showing ${failedRecipients.length} of ${failed} failures (rest truncated in the log).`}
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-success">All emails were sent successfully.</p>
        )}
      </Flex>
    </PageWrapper>
  );
};
